// src/components/Admin/UserManagement.js - FIXED VERSION
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Avatar,
  styled,
  LinearProgress,
  Grid,
  alpha,
  InputAdornment,
  Menu,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import {
  Edit,
  Delete,
  Block,
  CheckCircle,
  Search,
  FilterList,
  Person,
  MoreVert,
  Refresh,
  Add,
  AdminPanelSettings,
  LockReset,
  Visibility as GrantViewIcon,
  VisibilityOff as RevokeViewIcon,
} from '@mui/icons-material';
import { getUsers, suspendUser, activateUser, deleteUser } from '../../services/users';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
  success: '#4CAF50',
  warning: '#ff9800',
  error: '#f44336',
};

const GlassCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.8)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px 0 rgba(0, 0, 0, 0.36)'
    : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
}));

const StatsBox = ({ icon, label, value, color }) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: '10px',
      background: alpha(color, 0.1),
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '8px',
        background: `linear-gradient(45deg, ${color}, ${alpha(color, 0.7)})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { color: 'white', fontSize: 20 } })}
    </Box>
    <Box>
      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const SUPER_CLINICIAN_ROLES = [
  { value: 'clinician', label: 'Clinician' },
  { value: 'radiologist', label: 'Radiologist' },
  { value: 'technologist', label: 'Technologist' },
  { value: 'radiographer', label: 'Radiographer' },
  { value: 'sonographer', label: 'Sonographer' },
  { value: 'referring_physician', label: 'Referring Physician' },
  { value: 'nurse', label: 'Nurse' },
  { value: 'medical_records_officer', label: 'Medical Records Officer' },
];

function UserManagement() {
  const { user: currentUser } = useAuth();
  const isSuperClinician = currentUser?.role === 'super_clinician';

  const [users, setUsers] = useState([]); // Initialize as empty array
  const [filteredUsers, setFilteredUsers] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Add User dialog (covers all creatable roles)
  const [faDialogOpen, setFaDialogOpen] = useState(false);
  const [faForm, setFaForm] = useState({ firstName: '', lastName: '', email: '', role: isSuperClinician ? 'clinician' : 'facility_admin', facility: '', clinicalRole: 'clinician' });
  const [faLoading, setFaLoading] = useState(false);
  const [faError, setFaError] = useState('');
  const [faSuccess, setFaSuccess] = useState('');
  const [facilitiesList, setFacilitiesList] = useState([]);

  const FACILITY_REQUIRED_ROLES = [
    'radiographer', 'sonographer', 'referring_physician', 'nurse', 'medical_records_officer',
    'facility_admin', 'it_admin', 'compliance_officer', 'radiologist', 'clinician', 'technologist',
    'super_clinician',
    'lab_admin', 'lab_scientist', 'senior_lab_scientist', 'pathologist', 'consultant_pathologist',
    'histotechnologist', 'cytotechnologist', 'microbiologist', 'phlebotomist', 'quality_manager', 'inventory_manager'
  ];
  const ADMIN_CREATABLE_ROLES = [
    { value: 'facility_admin', label: 'Facility Admin' },
    { value: 'in_house_radiologist', label: 'In-House Radiologist (DycoVue Reader Portal)' },
    { value: 'radiologist', label: 'Radiologist' },
    { value: 'clinician', label: 'Clinician' },
    { value: 'super_clinician', label: 'Super Clinician' },
    { value: 'technologist', label: 'Technologist' },
    { value: 'radiographer', label: 'Radiographer' },
    { value: 'sonographer', label: 'Sonographer' },
    { value: 'referring_physician', label: 'Referring Physician' },
    { value: 'nurse', label: 'Nurse' },
    { value: 'medical_records_officer', label: 'Medical Records Officer' },
    { value: 'lab_admin', label: 'Laboratory Admin (LIS)' },
    { value: 'lab_scientist', label: 'Medical Lab Scientist (LIS)' },
    { value: 'senior_lab_scientist', label: 'Sr. Lab Scientist (LIS)' },
    { value: 'pathologist', label: 'Pathologist (LIS)' },
    { value: 'histotechnologist', label: 'Histotechnologist (LIS)' },
    { value: 'cytotechnologist', label: 'Cytotechnologist (LIS)' },
    { value: 'microbiologist', label: 'Microbiologist (LIS)' },
    { value: 'phlebotomist', label: 'Phlebotomist (LIS)' },
    { value: 'it_admin', label: 'IT Admin' },
    { value: 'compliance_officer', label: 'Compliance Officer' },
    { value: 'tech_support', label: 'Tech Support (DycoVue Staff)' },
    { value: 'product_lead', label: 'Product Lead (DycoVue Staff)' },
    { value: 'customer_success', label: 'Customer Success (DycoVue Staff)' },
    { value: 'compliance_internal', label: 'Compliance Officer (DycoVue Staff)' },
  ];

  // Derive a unified 'status' string from the two backend fields
  const deriveStatus = (user) => {
    if (user.approvalStatus === 'pending') return 'pending';
    if (user.approvalStatus === 'rejected') return 'rejected';
    if (!user.isActive) return 'suspended';
    return 'active';
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getUsers({ limit: 1000 });

      // Handle different response structures
      let usersList = [];

      if (Array.isArray(response)) {
        usersList = response;
      } else if (response?.users && Array.isArray(response.users)) {
        usersList = response.users;
      } else if (response?.data?.users && Array.isArray(response.data.users)) {
        usersList = response.data.users;
      } else {
        usersList = [];
      }

      // Attach computed status to each user
      usersList = usersList.map(u => ({ ...u, status: deriveStatus(u) }));

      setUsers(usersList);
      setFilteredUsers(usersList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users: ' + (err.message || 'Unknown error'));
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter users whenever search term or filters change
  useEffect(() => {
    if (!Array.isArray(users)) {
      console.error('Users is not an array:', users);
      setFilteredUsers([]);
      return;
    }

    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.facility?.facilityName?.toLowerCase().includes(searchLower) ||
          (Array.isArray(user.assignedFacilities) && user.assignedFacilities.some(f => (f.facilityName || f)?.toString().toLowerCase().includes(searchLower)))
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((user) => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, statusFilter, users]);

  const handleSuspendUser = async (userId) => {
    try {
      setError('');
      await suspendUser(userId);
      setSuccess('User suspended successfully');
      fetchUsers();
      handleMenuClose();
    } catch (err) {
      setError(err.message || 'Failed to suspend user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      setError('');
      await activateUser(userId);
      setSuccess('User activated successfully');
      fetchUsers();
      handleMenuClose();
    } catch (err) {
      setError(err.message || 'Failed to activate user');
    }
  };

  const handleResetPassword = async (user) => {
    handleMenuClose();
    if (!window.confirm(`Reset password for ${user.firstName} ${user.lastName}? A new temporary password will be generated and emailed to ${user.email}.`)) return;
    try {
      setError('');
      const res = await api.post('/auth/admin-reset-password', { userId: user._id });
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleToggleAllStudiesView = async (user) => {
    handleMenuClose();
    const next = !user.allowAllStudiesView;
    const label = next ? 'grant' : 'revoke';
    if (!window.confirm(`${next ? 'Grant' : 'Revoke'} "All Studies" view for ${user.firstName} ${user.lastName}?`)) return;
    try {
      setError('');
      await api.patch(`/users/${user._id}/radiologist-permissions`, { allowAllStudiesView: next });
      setSuccess(`"All Studies" view ${label}ed for ${user.firstName} ${user.lastName}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${label} permission`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      await deleteUser(selectedUser._id);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openFaDialog = async () => {
    setFaDialogOpen(true);
    setFaError('');
    setFaSuccess('');
    setFaForm({ firstName: '', lastName: '', email: '', role: 'facility_admin', facility: '', clinicalRole: 'clinician' });
    if (facilitiesList.length === 0) {
      try {
        const res = await api.get('/facilities', { params: { limit: 200, status: 'active' } });
        setFacilitiesList(res.data?.facilities || res.data?.data || []);
      } catch (_) {}
    }
  };

  const handleCreateUser = async () => {
    setFaLoading(true);
    setFaError('');
    try {
      const payload = { firstName: faForm.firstName, lastName: faForm.lastName, email: faForm.email, role: faForm.role };
      if (faForm.role === 'in_house_radiologist') {
        payload.role = 'radiologist';
        payload.radiologistType = 'dycovue';
      } else if (faForm.role === 'radiologist') {
        payload.radiologistType = 'facility';
      }
      if (faForm.facility) payload.facility = faForm.facility;
      if (faForm.role === 'facility_admin') payload.clinicalRole = faForm.clinicalRole;
      const res = await api.post('/auth/register-user', payload);
      setFaSuccess(res.data.message || 'User created successfully.');
      fetchUsers();
    } catch (err) {
      setFaError(err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to create user');
    } finally {
      setFaLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return teslaColors.success;
      case 'suspended': return teslaColors.error;
      case 'rejected': return teslaColors.error;
      case 'pending': return teslaColors.warning;
      default: return teslaColors.warning;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: teslaColors.error,
      facility_admin: '#1e40af',
      radiologist: teslaColors.primary,
      clinician: teslaColors.secondary,
      technologist: teslaColors.warning,
    };
    return colors[role] || teslaColors.primary;
  };

  const getRoleLabel = (role) => ({
    admin: 'Admin',
    facility_admin: 'Facility Admin',
    it_admin: 'IT Admin',
    compliance_officer: 'Compliance Officer',
    compliance_internal: 'Compliance (Internal)',
    radiologist: 'Radiologist',
    in_house_radiologist: 'In-House Radiologist',
    clinician: 'Clinician',
    super_clinician: 'Sr. Clinician',
    technologist: 'Technologist',
    radiographer: 'Radiographer',
    sonographer: 'Sonographer',
    referring_physician: 'Referring Physician',
    nurse: 'Nurse',
    medical_records_officer: 'Medical Records',
    lab_admin: 'Lab Admin',
    lab_scientist: 'Lab Scientist',
    senior_lab_scientist: 'Sr. Lab Scientist',
    pathologist: 'Pathologist',
    histotechnologist: 'Histotechnologist',
    cytotechnologist: 'Cytotechnologist',
    microbiologist: 'Microbiologist',
    phlebotomist: 'Phlebotomist',
    tech_support: 'Tech Support',
    product_lead: 'Product Lead',
    customer_success: 'Customer Success',
  }[role] || role);

  // Calculate stats - safely handle empty or non-array users
  const stats = React.useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return {
        total: 0,
        active: 0,
        suspended: 0,
        pending: 0,
      };
    }

    return {
      total: users.length,
      active: users.filter((u) => u.status === 'active').length,
      suspended: users.filter((u) => u.status === 'suspended').length,
      pending: users.filter((u) => u.status === 'pending').length,
    };
  }, [users]);

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          User Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage system users and their permissions
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Person sx={{ color: 'white', fontSize: 20 }} />}
            label="Total Users"
            value={stats.total}
            color={teslaColors.primary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<CheckCircle sx={{ color: 'white', fontSize: 20 }} />}
            label="Active Users"
            value={stats.active}
            color={teslaColors.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Block sx={{ color: 'white', fontSize: 20 }} />}
            label="Suspended"
            value={stats.suspended}
            color={teslaColors.error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Person sx={{ color: 'white', fontSize: 20 }} />}
            label="Pending Approval"
            value={stats.pending}
            color={teslaColors.warning}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <GlassCard sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '12px' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="facility_admin">Facility Admin</MenuItem>
                  <MenuItem value="it_admin">IT Admin</MenuItem>
                  <MenuItem value="compliance_officer">Compliance Officer</MenuItem>
                  <MenuItem value="radiologist">Radiologist</MenuItem>
                  <MenuItem value="clinician">Clinician</MenuItem>
                  <MenuItem value="super_clinician">Super Clinician</MenuItem>
                  <MenuItem value="technologist">Technologist</MenuItem>
                  <MenuItem value="radiographer">Radiographer</MenuItem>
                  <MenuItem value="sonographer">Sonographer</MenuItem>
                  <MenuItem value="referring_physician">Referring Physician</MenuItem>
                  <MenuItem value="nurse">Nurse</MenuItem>
                  <MenuItem value="medical_records_officer">Medical Records</MenuItem>
                  <MenuItem value="tech_support">Tech Support</MenuItem>
                  <MenuItem value="product_lead">Product Lead</MenuItem>
                  <MenuItem value="customer_success">Customer Success</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchUsers}
                  disabled={loading}
                  sx={{ borderRadius: '12px' }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AdminPanelSettings />}
                  onClick={openFaDialog}
                  sx={{ borderRadius: '12px', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d3a8a' } }}
                >
                  Add User
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Users Table */}
      <GlassCard>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ fontSize: '0.95rem' }}>
            All Users ({filteredUsers.length})
          </Typography>

          {loading ? (
            <LinearProgress />
          ) : filteredUsers.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Users Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'No users have been registered yet'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Facilities</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
                            }}
                          >
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {user.firstName} {user.lastName}
                            </Typography>
                            {user.institutionRole && (
                              <Typography variant="caption" color="textSecondary">
                                {user.institutionRole}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{user.email}</Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={getRoleLabel(user.role) || 'Clinician'}
                            size="small"
                            sx={{
                              background: alpha(getRoleBadge(user.role), 0.1),
                              color: getRoleBadge(user.role),
                              fontWeight: 600,
                              textTransform: 'capitalize',
                            }}
                          />
                          {user.role === 'radiologist' && user.radiologistType === 'dycovue' && (
                            <Tooltip title="In-House Radiologist (DycoVue Reader Portal)">
                              <Chip
                                label="In-House"
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha('#1e40af', 0.1), color: '#1e40af', fontWeight: 600 }}
                              />
                            </Tooltip>
                          )}
                          {user.role === 'radiologist' && user.allowAllStudiesView && (
                            <Tooltip title='Can view "All Studies"'>
                              <Chip
                                label="All Studies"
                                size="small"
                                icon={<GrantViewIcon sx={{ fontSize: '12px !important' }} />}
                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(teslaColors.success, 0.1), color: teslaColors.success, fontWeight: 600 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {user.role === 'radiologist' && user.radiologistType === 'dycovue' ? (
                            (() => {
                              const facNames = [];
                              if (Array.isArray(user.assignedFacilities) && user.assignedFacilities.length > 0) {
                                user.assignedFacilities.forEach((f) => {
                                  const name = typeof f === 'object' ? f.facilityName : f;
                                  if (name && !facNames.includes(name)) facNames.push(name);
                                });
                              }
                              if (user.facility?.facilityName && !facNames.includes(user.facility.facilityName)) {
                                facNames.unshift(user.facility.facilityName);
                              }
                              return facNames.length > 0 ? facNames.join(', ') : 'Not yet assigned';
                            })()
                          ) : (
                            user.facility?.facilityName || 'N/A'
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={user.status || 'Active'}
                          size="small"
                          sx={{
                            background: alpha(getStatusColor(user.status), 0.1),
                            color: getStatusColor(user.status),
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>

                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </GlassCard>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedUser?.status === 'active' ? (
          <MuiMenuItem onClick={() => handleSuspendUser(selectedUser._id)}>
            <Block fontSize="small" sx={{ mr: 1 }} />
            Suspend User
          </MuiMenuItem>
        ) : (
          <MuiMenuItem onClick={() => handleActivateUser(selectedUser._id)}>
            <CheckCircle fontSize="small" sx={{ mr: 1 }} />
            {selectedUser?.status === 'pending' ? 'Approve & Activate' : 'Activate User'}
          </MuiMenuItem>
        )}
        <MuiMenuItem onClick={() => handleResetPassword(selectedUser)}>
          <LockReset fontSize="small" sx={{ mr: 1 }} />
          Reset Password
        </MuiMenuItem>
        {selectedUser?.role === 'radiologist' && (
          <MuiMenuItem onClick={() => handleToggleAllStudiesView(selectedUser)}>
            {selectedUser?.allowAllStudiesView
              ? <><RevokeViewIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />Revoke "All Studies" View</>
              : <><GrantViewIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />Grant "All Studies" View</>
            }
          </MuiMenuItem>
        )}
        <MuiMenuItem onClick={() => handleDeleteClick(selectedUser)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete User
        </MuiMenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '440px' },
            maxWidth: '460px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
            Delete User
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
            Are you sure you want to delete <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleDeleteConfirm}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={faDialogOpen}
        onClose={() => setFaDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '620px', md: '660px' },
            maxWidth: '680px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <AdminPanelSettings sx={{ color: '#1e40af', fontSize: 22 }} />
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>Add User</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2.5, py: 1.5 }}>
          {faError && <Alert severity="error" onClose={() => setFaError('')} sx={{ py: 0.5 }}>{faError}</Alert>}
          {faSuccess && <Alert severity="success" sx={{ py: 0.5 }}>{faSuccess}</Alert>}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="First Name" required
                value={faForm.firstName}
                onChange={(e) => setFaForm({ ...faForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Last Name" required
                value={faForm.lastName}
                onChange={(e) => setFaForm({ ...faForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Email Address" type="email" required
                value={faForm.email}
                onChange={(e) => setFaForm({ ...faForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={faForm.role}
                  onChange={(e) => setFaForm({ ...faForm, role: e.target.value })}
                >
                  {(isSuperClinician ? SUPER_CLINICIAN_ROLES : ADMIN_CREATABLE_ROLES).map(r => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Super clinicians are locked to their own facility — no facility picker needed */}
            {!isSuperClinician && FACILITY_REQUIRED_ROLES.includes(faForm.role) && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Assign to Facility</InputLabel>
                  <Select
                    label="Assign to Facility"
                    value={faForm.facility}
                    onChange={(e) => setFaForm({ ...faForm, facility: e.target.value })}
                  >
                    {facilitiesList.map((f) => (
                      <MenuItem key={f._id} value={f._id}>{f.facilityName} ({f.facilityId})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {!isSuperClinician && faForm.role === 'in_house_radiologist' && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Primary Facility (Optional)</InputLabel>
                  <Select
                    label="Primary Facility (Optional)"
                    value={faForm.facility}
                    onChange={(e) => setFaForm({ ...faForm, facility: e.target.value })}
                  >
                    <MenuItem value=""><em>None (Reads across all assigned hospitals)</em></MenuItem>
                    {facilitiesList.map((f) => (
                      <MenuItem key={f._id} value={f._id}>{f.facilityName} ({f.facilityId})</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.72rem' }}>
                    In-house radiologists read across dedicated hospitals in the DycoVue Reader Portal.
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            {faForm.role === 'facility_admin' && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Clinical Role</InputLabel>
                  <Select
                    label="Clinical Role"
                    value={faForm.clinicalRole}
                    onChange={(e) => setFaForm({ ...faForm, clinicalRole: e.target.value })}
                  >
                    <MenuItem value="clinician">Facility Admin (Clinician) — creates orders, views worklist</MenuItem>
                    <MenuItem value="radiologist">Facility Admin (Radiologist) — views PACS, writes reports</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem' }}>
            Login credentials will be emailed to the user automatically.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setFaDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleCreateUser}
            disabled={faLoading || !faForm.firstName || !faForm.lastName || !faForm.email || (!isSuperClinician && FACILITY_REQUIRED_ROLES.includes(faForm.role) && !faForm.facility) || !!faSuccess}
            sx={{ borderRadius: '8px', bgcolor: '#1e40af', textTransform: 'none', px: 2.5, '&:hover': { bgcolor: '#1d3a8a' } }}
          >
            {faLoading ? 'Creating…' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;